import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Search,
  Book,
  Video,
  FileText,
  MessageCircle,
  ChevronRight,
  ExternalLink,
  HelpCircle,
  Zap,
  Settings,
  Users,
  BarChart3,
  Shield,
  CreditCard,
  Play,
  Clock,
  ArrowRight,
  Sparkles,
  Image,
  Globe,
  Webhook,
  Palette,
  Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const HelpCenterPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const categories = [
    { icon: Zap, title: 'Getting Started', count: 12, color: 'emerald', description: 'Quick start guides and basics' },
    { icon: Calendar, title: 'Content Calendar', count: 18, color: 'blue', description: 'Scheduling, AI auto-fill, and planning posts' },
    { icon: Users, title: 'Team & Collaboration', count: 15, color: 'purple', description: 'Real-time collaboration, roles, and approvals' },
    { icon: BarChart3, title: 'Analytics', count: 10, color: 'amber', description: 'Understanding your metrics' },
    { icon: Settings, title: 'Account Settings', count: 8, color: 'slate', description: 'Profile and preferences' },
    { icon: CreditCard, title: 'Billing & Plans', count: 6, color: 'pink', description: 'Subscriptions and payments' },
    { icon: Sparkles, title: 'AI Features', count: 14, color: 'violet', description: 'AI captions, predictions, auto-fill, and ideas' },
    { icon: Target, title: 'Social Listening', count: 8, color: 'cyan', description: 'Brand monitoring and sentiment tracking' },
    { icon: Globe, title: 'Workflows & Testing', count: 10, color: 'orange', description: 'Automated workflows and A/B testing' }
  ];

  const popularArticles = [
    { title: 'How to connect your social media accounts', category: 'Getting Started', views: '15.2K' },
    { title: 'Creating and scheduling your first post', category: 'Content Calendar', views: '12.8K' },
    { title: 'Using AI Auto-fill to generate a week of posts', category: 'AI Features', views: '11.5K' },
    { title: 'Setting up Social Listening topics and keywords', category: 'Social Listening', views: '10.9K' },
    { title: 'Creating your first A/B test to optimize posts', category: 'Workflows & Testing', views: '10.3K' },
    { title: 'Using Post Performance Predictions before publishing', category: 'AI Features', views: '9.8K' },
    { title: 'Understanding your analytics dashboard', category: 'Analytics', views: '10.5K' },
    { title: 'Real-time collaboration with your team on posts', category: 'Team & Collaboration', views: '9.3K' },
    { title: 'Building automated workflows for post management', category: 'Workflows & Testing', views: '8.7K' },
    { title: 'Using AI to generate content ideas with industry filters', category: 'AI Features', views: '8.5K' }
  ];

  const videoTutorials = [
    {
      id: 1,
      title: 'Getting Started with StelliSocial',
      description: 'Complete beginner guide to set up your account and start posting',
      duration: '0:04',
      videoSrc: '/videos/demo-video-1.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=400&h=225&fit=crop',
      category: 'Getting Started',
      views: '25.4K',
      chapters: [
        { time: '0:00', title: 'Creating Your Account' },
        { time: '0:01', title: 'Dashboard Overview' },
        { time: '0:02', title: 'Connecting Accounts' },
        { time: '0:03', title: 'Your First Post' }
      ]
    },
    {
      id: 2,
      title: 'Mastering the Content Calendar',
      description: 'Learn to plan, schedule, and organize your content like a pro',
      duration: '0:04',
      videoSrc: '/videos/demo-calendar.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1506784365847-bbad939e9335?w=400&h=225&fit=crop',
      category: 'Content Calendar',
      views: '18.7K',
      chapters: [
        { time: '0:00', title: 'Calendar Views Explained' },
        { time: '0:01', title: 'Creating Posts' },
        { time: '0:02', title: 'Drag & Drop Scheduling' },
        { time: '0:03', title: 'Bulk Actions' }
      ]
    },
    {
      id: 3,
      title: 'Analytics Deep Dive',
      description: 'Understand your metrics and optimize your social strategy',
      duration: '0:04',
      videoSrc: '/videos/demo-video-2.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop',
      category: 'Analytics',
      views: '14.2K',
      chapters: [
        { time: '0:00', title: 'Analytics Dashboard' },
        { time: '0:01', title: 'Post Performance' },
        { time: '0:02', title: 'Audience Insights' },
        { time: '0:03', title: 'Building Reports' }
      ]
    },
    {
      id: 4,
      title: 'AI Content Generation',
      description: 'Use AI to create captions, hashtags, and video content',
      duration: '0:04',
      videoSrc: '/videos/demo-video-3.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=225&fit=crop',
      category: 'AI Tools',
      views: '22.1K',
      chapters: [
        { time: '0:00', title: 'AI Caption Writer' },
        { time: '0:01', title: 'Hashtag Generator' },
        { time: '0:02', title: 'Content Ideas' },
        { time: '0:03', title: 'Video Generation' }
      ]
    },
    {
      id: 5,
      title: 'Team Collaboration Features',
      description: 'Set up your team, roles, and approval workflows',
      duration: '0:04',
      videoSrc: '/videos/demo-video-4.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=225&fit=crop',
      category: 'Team & Collaboration',
      views: '11.8K',
      chapters: [
        { time: '0:00', title: 'Inviting Members' },
        { time: '0:01', title: 'Role Permissions' },
        { time: '0:02', title: 'Approval Workflows' },
        { time: '0:03', title: 'Comments & Feedback' }
      ]
    },
    {
      id: 6,
      title: 'Connecting Social Platforms',
      description: 'Step-by-step guide to connect all 11+ supported platforms',
      duration: '0:04',
      videoSrc: '/videos/demo-video-5.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=400&h=225&fit=crop',
      category: 'Getting Started',
      views: '19.5K',
      chapters: [
        { time: '0:00', title: 'Supported Platforms' },
        { time: '0:01', title: 'OAuth Connection' },
        { time: '0:02', title: 'Managing Accounts' },
        { time: '0:03', title: 'Troubleshooting' }
      ]
    },
    {
      id: 7,
      title: 'Brand Guidelines Setup',
      description: 'Create and enforce brand consistency across all posts',
      duration: '0:04',
      videoSrc: '/videos/demo-video-1.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1558655146-d09347e92766?w=400&h=225&fit=crop',
      category: 'Brand',
      views: '8.9K',
      chapters: [
        { time: '0:00', title: 'Creating Guidelines' },
        { time: '0:01', title: 'Color & Typography' },
        { time: '0:02', title: 'Voice & Tone' },
        { time: '0:03', title: 'Content Validation' }
      ]
    },
    {
      id: 8,
      title: 'Media Library Management',
      description: 'Organize, edit, and use your images and videos effectively',
      duration: '0:04',
      videoSrc: '/videos/demo-video-2.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1611532736597-de2d4265fba3?w=400&h=225&fit=crop',
      category: 'Content',
      views: '7.3K',
      chapters: [
        { time: '0:00', title: 'Uploading Media' },
        { time: '0:01', title: 'Organizing Files' },
        { time: '0:02', title: 'Using in Posts' },
        { time: '0:03', title: 'Storage Tips' }
      ]
    },
    {
      id: 9,
      title: 'Custom Webhooks & Channels',
      description: 'Integrate StelliSocial with any platform using webhooks',
      duration: '0:04',
      videoSrc: '/videos/demo-video-3.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400&h=225&fit=crop',
      category: 'Advanced',
      views: '5.6K',
      chapters: [
        { time: '0:00', title: 'What are Webhooks' },
        { time: '0:01', title: 'Creating Channels' },
        { time: '0:02', title: 'Payload Templates' },
        { time: '0:03', title: 'Testing & Debugging' }
      ]
    },
    {
      id: 10,
      title: 'Competitor Analysis',
      description: 'Track competitors and get actionable insights',
      duration: '0:04',
      videoSrc: '/videos/demo-video-4.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=225&fit=crop',
      category: 'Analytics',
      views: '9.2K',
      chapters: [
        { time: '0:00', title: 'Adding Competitors' },
        { time: '0:01', title: 'Performance Tracking' },
        { time: '0:02', title: 'Content Analysis' },
        { time: '0:03', title: 'Strategic Insights' }
      ]
    },
    {
      id: 11,
      title: 'Post Templates',
      description: 'Save time by creating and using reusable post templates',
      duration: '0:04',
      videoSrc: '/videos/demo-video-5.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1586281380349-632531db7ed4?w=400&h=225&fit=crop',
      category: 'Content',
      views: '6.8K',
      chapters: [
        { time: '0:00', title: 'Creating Templates' },
        { time: '0:01', title: 'Template Variables' },
        { time: '0:02', title: 'Using Templates' },
        { time: '0:03', title: 'Best Practices' }
      ]
    },
    {
      id: 12,
      title: 'Building Custom Reports',
      description: 'Create professional reports for clients or stakeholders',
      duration: '0:04',
      videoSrc: '/videos/demo-calendar.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=400&h=225&fit=crop',
      category: 'Analytics',
      views: '8.4K',
      chapters: [
        { time: '0:00', title: 'Report Builder' },
        { time: '0:01', title: 'Selecting Metrics' },
        { time: '0:02', title: 'Customization' },
        { time: '0:03', title: 'Exporting & Sharing' }
      ]
    },
    {
      id: 13,
      title: 'Social Listening & Brand Monitoring',
      description: 'Track brand mentions, keywords, and sentiment across social media platforms',
      duration: '0:04',
      videoSrc: '/videos/demo-video-1.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=400&h=225&fit=crop',
      category: 'Social Listening',
      views: '10.9K',
      chapters: [
        { time: '0:00', title: 'Creating Listening Topics' },
        { time: '0:01', title: 'Dashboard & Sentiment' },
        { time: '0:02', title: 'Filtering Mentions' },
        { time: '0:03', title: 'AI Brand Analysis' }
      ]
    },
    {
      id: 14,
      title: 'Automated Workflows',
      description: 'Build if-this-then-that rules to automate post approvals, labeling, and notifications',
      duration: '0:04',
      videoSrc: '/videos/demo-video-2.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1518432031352-d6fc5c10da5a?w=400&h=225&fit=crop',
      category: 'Workflows & Testing',
      views: '8.7K',
      chapters: [
        { time: '0:00', title: 'Workflow Templates' },
        { time: '0:01', title: 'Custom Workflows' },
        { time: '0:02', title: 'Triggers & Actions' },
        { time: '0:03', title: 'Testing & Logs' }
      ]
    },
    {
      id: 15,
      title: 'A/B Testing for Posts',
      description: 'Compare post variants to find what resonates best with your audience',
      duration: '0:04',
      videoSrc: '/videos/demo-video-3.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=225&fit=crop',
      category: 'Workflows & Testing',
      views: '10.3K',
      chapters: [
        { time: '0:00', title: 'Creating A/B Tests' },
        { time: '0:01', title: 'AI Variant Generation' },
        { time: '0:02', title: 'Reading Results' },
        { time: '0:03', title: 'AI Analysis & Insights' }
      ]
    },
    {
      id: 16,
      title: 'Post Performance Predictions',
      description: 'Use AI to predict engagement before publishing and get improvement suggestions',
      duration: '0:04',
      videoSrc: '/videos/demo-video-4.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop',
      category: 'AI Features',
      views: '9.8K',
      chapters: [
        { time: '0:00', title: 'Accessing Predictions' },
        { time: '0:01', title: 'Understanding Scores' },
        { time: '0:02', title: 'Score Factors' },
        { time: '0:03', title: 'Applying AI Suggestions' }
      ]
    },
    {
      id: 17,
      title: 'Real-time Collaboration',
      description: 'Work on posts together with live comments, typing indicators, and presence',
      duration: '0:04',
      videoSrc: '/videos/demo-video-5.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=400&h=225&fit=crop',
      category: 'Team & Collaboration',
      views: '9.3K',
      chapters: [
        { time: '0:00', title: 'Collaboration Panel' },
        { time: '0:01', title: 'Live Comments' },
        { time: '0:02', title: 'Typing Indicators' },
        { time: '0:03', title: 'Working Together' }
      ]
    },
    {
      id: 18,
      title: 'AI Calendar Auto-fill',
      description: 'Generate a full week of scheduled posts with AI in one click',
      duration: '0:04',
      videoSrc: '/videos/demo-calendar.mp4',
      thumbnail: 'https://images.unsplash.com/photo-1506784365847-bbad939e9335?w=400&h=225&fit=crop',
      category: 'AI Features',
      views: '11.5K',
      chapters: [
        { time: '0:00', title: 'Opening Auto-fill' },
        { time: '0:01', title: 'Setting Theme & Tone' },
        { time: '0:02', title: 'Reviewing Generated Plan' },
        { time: '0:03', title: 'Applying Posts to Calendar' }
      ]
    }
  ];

  const filteredVideos = videoTutorials.filter(video =>
    video.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    video.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
              <Link to="/help" className="text-emerald-600 font-medium">Help Center</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Badge className="bg-emerald-100 text-emerald-700 mb-4">Help Center</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            How can we help you?
          </h1>
          <p className="text-xl text-slate-600 mb-8">
            Find answers, watch tutorials, and get the most out of StelliSocial
          </p>
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              placeholder="Search for articles, tutorials, or topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-14 text-lg rounded-xl border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
            />
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Tabs defaultValue="videos" className="space-y-8">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-3">
              <TabsTrigger value="videos" className="flex items-center gap-2">
                <Video className="h-4 w-4" />
                Video Tutorials
              </TabsTrigger>
              <TabsTrigger value="articles" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Articles
              </TabsTrigger>
              <TabsTrigger value="categories" className="flex items-center gap-2">
                <Book className="h-4 w-4" />
                Categories
              </TabsTrigger>
            </TabsList>

            {/* Video Tutorials Tab */}
            <TabsContent value="videos" className="space-y-8">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Video Tutorials</h2>
                  <p className="text-slate-600">Learn StelliSocial with step-by-step video guides</p>
                </div>
                <Link to="/demo">
                  <Button variant="outline">
                    <Play className="h-4 w-4 mr-2" />
                    Watch Full Demo
                  </Button>
                </Link>
              </div>

              {/* Featured Video */}
              <div className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-2xl p-8 text-white">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <Badge className="bg-white/20 text-white border-white/30 mb-4">Featured</Badge>
                    <h3 className="text-2xl font-bold mb-2">Complete Platform Overview</h3>
                    <p className="text-emerald-100 mb-6">
                      New to StelliSocial? Start here! This comprehensive guide covers everything 
                      you need to know to get started.
                    </p>
                    <Button 
                      className="bg-white text-emerald-600 hover:bg-emerald-50"
                      onClick={() => setSelectedVideo(videoTutorials[0])}
                    >
                      <Play className="h-4 w-4 mr-2" />
                      Watch Now (5:32)
                    </Button>
                  </div>
                  <div 
                    className="relative rounded-xl overflow-hidden cursor-pointer group"
                    onClick={() => setSelectedVideo(videoTutorials[0])}
                  >
                    <img 
                      src={videoTutorials[0].thumbnail}
                      alt="Getting Started"
                      className="w-full aspect-video object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/40 transition-colors">
                      <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                        <Play className="h-6 w-6 text-emerald-600 ml-1" fill="currentColor" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Video Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredVideos.map((video) => (
                  <motion.div
                    key={video.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                  >
                    <Card 
                      className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                      onClick={() => setSelectedVideo(video)}
                    >
                      <div className="relative">
                        <img 
                          src={video.thumbnail}
                          alt={video.title}
                          className="w-full aspect-video object-cover group-hover:scale-105 transition-transform"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Play className="h-5 w-5 text-emerald-600 ml-0.5" fill="currentColor" />
                          </div>
                        </div>
                        <div className="absolute bottom-2 right-2 bg-black/70 px-2 py-1 rounded text-white text-xs flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {video.duration}
                        </div>
                        <Badge className="absolute top-2 left-2 bg-white/90 text-slate-700">
                          {video.category}
                        </Badge>
                      </div>
                      <CardContent className="p-4">
                        <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-emerald-600 transition-colors">
                          {video.title}
                        </h3>
                        <p className="text-sm text-slate-500 line-clamp-2">{video.description}</p>
                        <div className="flex items-center gap-2 mt-3 text-xs text-slate-400">
                          <span>{video.views} views</span>
                          <span>•</span>
                          <span>{video.chapters.length} chapters</span>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            {/* Articles Tab */}
            <TabsContent value="articles" className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Popular Articles</h2>
                <p className="text-slate-600">Most viewed help articles this month</p>
              </div>

              <div className="space-y-4">
                {popularArticles.map((article, index) => (
                  <motion.div
                    key={article.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="hover:shadow-md transition-shadow cursor-pointer">
                      <CardContent className="p-4 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                            <FileText className="h-5 w-5 text-emerald-600" />
                          </div>
                          <div>
                            <h3 className="font-medium text-slate-900 hover:text-emerald-600 transition-colors">
                              {article.title}
                            </h3>
                            <p className="text-sm text-slate-500">{article.category}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-slate-400">{article.views} views</span>
                          <ChevronRight className="h-5 w-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </TabsContent>

            {/* Categories Tab */}
            <TabsContent value="categories" className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2">Browse by Category</h2>
                <p className="text-slate-600">Find help articles organized by topic</p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((category, index) => (
                  <motion.div
                    key={category.title}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className="hover:shadow-lg transition-shadow cursor-pointer group">
                      <CardContent className="p-6">
                        <div className={`w-12 h-12 bg-${category.color}-100 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <category.icon className={`h-6 w-6 text-${category.color}-600`} />
                        </div>
                        <h3 className="text-lg font-semibold text-slate-900 mb-1 group-hover:text-emerald-600 transition-colors">
                          {category.title}
                        </h3>
                        <p className="text-slate-500 text-sm mb-3">{category.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-400">{category.count} articles</span>
                          <ArrowRight className="h-4 w-4 text-emerald-600 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <MessageCircle className="h-12 w-12 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Still need help?</h2>
          <p className="text-slate-600 mb-6">
            Our support team is here to assist you with any questions
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button className="bg-emerald-500 hover:bg-emerald-600">
              <MessageCircle className="h-4 w-4 mr-2" />
              Contact Support
            </Button>
            <Link to="/community">
              <Button variant="outline">
                <Users className="h-4 w-4 mr-2" />
                Join Community
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Video Player Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden">
          {selectedVideo && (
            <>
              <div className="aspect-video bg-slate-900 relative">
                <video
                  src={selectedVideo.videoSrc}
                  className="w-full h-full object-cover"
                  controls
                  autoPlay
                  poster={selectedVideo.thumbnail}
                  key={selectedVideo.id}
                >
                  <source src={selectedVideo.videoSrc} type="video/mp4" />
                </video>
              </div>
              <div className="p-6">
                <Badge className="mb-2">{selectedVideo.category}</Badge>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{selectedVideo.title}</h3>
                <p className="text-slate-600 mb-4">{selectedVideo.description}</p>
                
                <div className="border-t pt-4">
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Chapters</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {selectedVideo.chapters.map((chapter) => (
                      <button
                        key={chapter.time}
                        className="flex items-center gap-2 text-left p-2 rounded-lg hover:bg-slate-100 transition-colors"
                      >
                        <span className="text-emerald-600 text-xs font-mono bg-emerald-50 px-2 py-1 rounded">
                          {chapter.time}
                        </span>
                        <span className="text-slate-700 text-sm">{chapter.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpCenterPage;
